import { useState } from 'react';
import { useGetIdentity, useNotify } from 'react-admin';
import { Send, Loader2, Wand2 } from 'lucide-react';
import { Button as ShadButton } from '@/components/ui/button';
import { translateText } from '../funerals/components/translateApi';
import { getToken } from '../authentication/authProvider';

const API_URL = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

export function SmsReplyBar({ phoneNumber, contactName }: { phoneNumber: string; contactName?: string }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [translating, setTranslating] = useState(false);
  const { identity } = useGetIdentity();
  const notify = useNotify();

  const canSend = message.trim() && !sending;

  const handleTranslateToFrench = async () => {
    if (!message.trim()) return;
    setTranslating(true);
    try {
      const translated = await translateText(message.trim(), 'fr');
      setMessage(translated);
    } catch (err: any) {
      console.error('Translation failed:', err);
      notify(`Translation failed: ${err?.message || 'Unknown error'}`, { type: 'warning' });
    } finally {
      setTranslating(false);
    }
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const token = await getToken();
      const response = await fetch(`${baseUrl}/aircall/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          phoneNumber,
          message: message.trim(),
          senderName: identity?.fullName || undefined,
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || `Failed to send (${response.status})`);
      }
      setMessage('');
      notify('SMS sent', { type: 'success' });
    } catch (err: any) {
      console.error('Failed to send SMS:', err);
      notify(`Failed to send: ${err?.message || 'Unknown error'}`, { type: 'warning' });
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && canSend) {
      e.preventDefault();
      void handleSend();
    }
  };

  return (
    <div className="border-t bg-background p-2 sm:p-3 space-y-2 shrink-0">
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={`SMS to ${contactName || phoneNumber}...`}
        disabled={sending}
        rows={2}
        className="w-full resize-none rounded-lg border bg-muted/30 px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
      />
      <div className="flex items-center justify-end gap-1.5">
        <ShadButton
          size="sm"
          variant="outline"
          disabled={!message.trim() || translating || sending}
          onClick={handleTranslateToFrench}
          className="gap-1.5"
          title="Translate to French"
        >
          {translating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
          FR
        </ShadButton>
        <ShadButton size="sm" disabled={!canSend} onClick={handleSend} className="gap-1.5">
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Send
        </ShadButton>
      </div>
    </div>
  );
}
